/**
 * useFirestoreBrands — cached hook for canonicalized brand suggestions
 *
 * Feeds the `firestoreBrands` prop on `BrandAutocomplete`. The `brands`
 * collection is small (~1,600 docs) and rarely mutated, so a single
 * process-lifetime fetch shared across every Add[Type] modal is fine.
 * We cache the fetch promise itself (not just the resolved value) so
 * simultaneous callers on first mount coalesce into one Firestore
 * round-trip.
 *
 * The list surfaces suggestions in the autocomplete dropdown so users
 * typing "essi" get "Essie" as a canonical option — the upstream fix
 * to brand-name variant drift. Only these three fields make it to the
 * component; the full Brand doc is heavier than the dropdown needs.
 */

import { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

export interface FirestoreBrandSuggestion {
  name: string;
  status?: string;
  curationStatus?: string;
}

let cachedPromise: Promise<FirestoreBrandSuggestion[]> | null = null;

/** Fetch and cache the brands list at module scope. First caller triggers
 *  the Firestore read; every caller thereafter (including simultaneous
 *  mounts on first paint) awaits the same promise. Call invalidate() to
 *  force a re-fetch if the underlying data changes mid-session — no
 *  invalidation call sites today; here for a future admin action. */
export function loadFirestoreBrands(): Promise<FirestoreBrandSuggestion[]> {
  if (cachedPromise) return cachedPromise;
  cachedPromise = (async () => {
    const snap = await getDocs(collection(db, 'brands'));
    const out: FirestoreBrandSuggestion[] = [];
    for (const d of snap.docs) {
      const data = d.data() as Record<string, unknown>;
      const name = typeof data.name === 'string' ? data.name.trim() : '';
      if (!name) continue;
      out.push({
        name,
        status: typeof data.status === 'string' ? data.status : undefined,
        curationStatus:
          typeof data.curationStatus === 'string'
            ? data.curationStatus
            : undefined,
      });
    }
    // Alphabetize once; consumers can re-sort by relevance vs input.
    out.sort((a, b) => a.name.localeCompare(b.name));
    return out;
  })().catch((err) => {
    // Reset on failure so a retry can happen next time the hook mounts.
    cachedPromise = null;
    throw err;
  });
  return cachedPromise;
}

export function invalidateFirestoreBrandsCache() {
  cachedPromise = null;
}

/** React hook wrapper — returns the loaded list or an empty array while
 *  the cache warms. Empty is a safe default for autocomplete (component
 *  still functions off its other sources). */
export function useFirestoreBrands(): FirestoreBrandSuggestion[] {
  const [brands, setBrands] = useState<FirestoreBrandSuggestion[]>([]);
  useEffect(() => {
    let cancelled = false;
    loadFirestoreBrands()
      .then((list) => {
        if (!cancelled) setBrands(list);
      })
      .catch((err) => {
        console.warn('[useFirestoreBrands] load failed:', err);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  return brands;
}
